import { PermutiveClient, PermutiveEventProperties } from './permutiveEvent';

/**
 * Permutive-shaped `LinkClick` event (Mosaic Phase 6, DAP-114765).
 *
 * Emitted when a qualifying link is clicked. Properties:
 *   - `client`   : common client block (added by the event framework)
 *   - `dest_url` : the link's destination URL
 */

export type LinkClickProperties = PermutiveEventProperties & {
  client?: PermutiveClient;
  dest_url: string;
};

/** Walk up from the click target to the nearest anchor carrying an href. */
export function findAnchor(target: EventTarget | null): HTMLAnchorElement | null {
  const el = target as (Element & { closest?: (s: string) => Element | null }) | null;
  if (el && typeof el.closest === 'function') {
    return el.closest('a[href]') as HTMLAnchorElement | null;
  }
  return null;
}

/**
 * Resolve the destination URL for a qualifying link click, or `null` when the
 * click should not produce a LinkClick event. Non-qualifying clicks are:
 *   - clicks already cancelled by an earlier handler (`defaultPrevented`),
 *   - clicks not on (or inside) an anchor with an href,
 *   - non-navigational schemes (mailto:, tel:, javascript:, etc.),
 *   - pure same-page hash changes (same origin + path + query, only #hash).
 *
 * Note: we resolve synchronously on the capture phase so an outbound click is
 * queued before the page unloads. That means a handler which cancels the
 * navigation *later* (in the bubble phase) cannot be detected here — deferring
 * the check would risk dropping genuine outbound LinkClicks, which is the worse
 * trade for Mosaic.
 */
export function resolveLinkClickDest(event: { target: EventTarget | null; defaultPrevented?: boolean }): string | null {
  if (event.defaultPrevented) {
    return null;
  }

  const anchor = findAnchor(event.target);
  if (!anchor) {
    return null;
  }

  const href = anchor.href;
  if (!href) {
    return null;
  }

  const scheme = (anchor.protocol || '').toLowerCase();
  if (scheme && scheme !== 'http:' && scheme !== 'https:') {
    return null;
  }

  const loc = typeof window !== 'undefined' ? window.location : undefined;
  const isSamePageHash =
    Boolean(anchor.hash) &&
    !!loc &&
    anchor.origin === loc.origin &&
    anchor.pathname === loc.pathname &&
    anchor.search === loc.search;
  if (isSamePageHash) {
    return null;
  }

  return href;
}

/** Assemble the `LinkClick` properties. `client` is normally injected by the
 * event framework and only set here when explicitly overridden. */
export function buildLinkClickProperties(args: { dest_url: string; client?: PermutiveClient }): LinkClickProperties {
  const properties: LinkClickProperties = { dest_url: args.dest_url };
  if (args.client) {
    properties.client = args.client;
  }
  return properties;
}
